import { useState } from 'react';
import {
  acknowledgeMemberRecommendation,
  deletePhotos,
  getHousehold,
  getMemberRecommendations,
  getMemberScore,
  getMemberState,
  listPhotos,
  photoContentTypeOf,
  avatarContentTypeOf,
  removeAvatar,
  setAvatarFromPhoto,
  sha256Hex,
  uploadAvatar,
  uploadPhotos,
  type HouseholdMember,
  type PhotoContentType,
} from '../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import {
  faceScanOwed,
  focusCropStyle,
  limitDetailOf,
  memberDisplayName,
  memberInitials,
  memberNextCopy,
  pfpExpired,
  photoLimitCopy,
} from '../flow/member-space.js';
import { recommendationHref } from '../flow/recommendations.js';
import {
  Async,
  Badge,
  Bool,
  Button,
  Failure,
  KeyValue,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

/**
 * The family member screen — `#/member/<personId>`.
 *
 * ONE IDEA BEHIND ALL OF IT: a photo has a SUBJECT, and the subject is a
 * person, not an account (spec 2026-09-02). Each protected person has their own
 * photo space and their own allowance, and the manager uploads INTO it rather
 * than into their own grid.
 *
 * TWO KINDS OF MEMBER, ONE FIELD THAT TELLS THEM APART, AND IT IS NOT COMPUTED
 * HERE. `photos_visible` comes off the roster and already accounts for
 * `has_own_device` AND whether this caller owns the household — a household
 * MEMBER reading the roster sees the other members and must not be offered
 * their photos. Re-deriving it from `has_own_device` is the specific mistake
 * the mobile contract names, so this screen reads the field and computes
 * nothing. The same field gates the profile-picture write, because the api
 * authorises both through one predicate.
 *
 * NO HIT DETAIL ANYWHERE ON IT (P20). What a manager gets is coverage, status
 * and COUNTS: `active_hits` is a number and there is no route from here into
 * another person's findings. That is also why the `ask` voice below renders no
 * navigation — the one card carrying a target in that voice would be
 * respond_to_hits pointing at `Reports`.
 */

/** Which member row on the roster this screen is about, or why we cannot say. */
function memberOf(
  members: HouseholdMember[],
  personId: string,
): HouseholdMember | undefined {
  return members.find((entry) => entry.person_id === personId);
}

/**
 * The signed-until line. A URL with no visible expiry is a bearer capability
 * whose lifetime nobody can see, which is the whole reason `pfp_expires_at`
 * is on the payload — so an unreadable stamp says so rather than printing
 * "Invalid Date".
 */
function expiryLabel(expiresAt: string | null): string {
  if (expiresAt === null) return 'no expiry given';
  const stamp = new Date(expiresAt);
  if (Number.isNaN(stamp.getTime())) return `unreadable expiry (${expiresAt})`;
  return `signed until ${stamp.toLocaleTimeString()}`;
}

function Pfp({ member, size }: { member: HouseholdMember; size: number }) {
  const usable = member.pfp_url !== null && !pfpExpired(member.pfp_expires_at);
  if (!usable) {
    // `null` IS THE ORDINARY STATE (P24), not an error and not a retry.
    return (
      <span className="pfp pfp-initials" style={{ width: size, height: size, fontSize: size / 2.6 }}>
        {memberInitials(member)}
      </span>
    );
  }
  const crop = focusCropStyle(member.pfp_focus);
  return (
    <img
      className="pfp"
      style={{ width: size, height: size, objectFit: crop.objectFit, objectPosition: crop.objectPosition }}
      src={member.pfp_url ?? ''}
      alt=""
    />
  );
}

export function MemberScreen({ personId }: { personId: string }) {
  const household = useApi(getHousehold, [personId]);

  return (
    <Async state={household} empty="no household — a plan with seats creates one">
      {(data) => {
        const member = memberOf(data.members, personId);
        if (member === undefined) {
          /**
           * NOT FOUND AND NOT PERMITTED ARE THE SAME ANSWER, deliberately.
           * The api gives an identical 403 for an unmanageable subject, a
           * person outside the household, and a person id that does not
           * exist — telling them apart would let a caller discover which ids
           * are real (P2). This screen must not undo that by guessing.
           */
          return (
            <Screen title="Member">
              <Note>
                No member with that id is on your household roster. An id that is not yours and an
                id that does not exist look identical here, on purpose.
              </Note>
              <Row>
                <a className="action secondary" href="#/family">
                  Back to Family
                </a>
              </Row>
            </Screen>
          );
        }
        return <MemberDetail member={member} onChanged={household.reload} />;
      }}
    </Async>
  );
}

function MemberDetail({
  member,
  onChanged,
}: {
  member: HouseholdMember;
  onChanged: () => void;
}) {
  const personId = member.person_id;
  const name = memberDisplayName(member);
  const firstName = member.first_name ?? name;

  /**
   * A photo landing changes THREE reads: their grid, their `next` (which flips
   * off 'photos' the moment one exists), and the picker the profile picture is
   * chosen from. One nonce rather than three callbacks, because the panels do
   * not otherwise know about each other and a missed one shows as a picker
   * with nothing in it right after an upload.
   */
  const [photoNonce, setPhotoNonce] = useState(0);
  const photosChanged = () => setPhotoNonce((value) => value + 1);

  const state = useApi(() => getMemberState(personId), [personId, photoNonce]);
  const score = useApi(() => getMemberScore(personId), [personId]);
  const recommendations = useApi(() => getMemberRecommendations(personId), [personId]);

  return (
    <Screen
      title={name}
      lede={`Their own photo space, their own allowance, their own score. Every call here names them with subject_person_id, which the api authorises against your session — it is an input we check, never a way to address data.`}
    >
      <Panel>
        <div className="member-head">
          <Pfp member={member} size={72} />
          <div>
            <h2 className="member-name">{name}</h2>
            <Row>
              <Badge>{member.role}</Badge>
              <Badge tone={member.has_own_device ? undefined : 'warn'}>
                {member.has_own_device ? 'own device' : 'on-device member'}
              </Badge>
              <Badge tone={member.covered ? 'ok' : 'warn'}>
                {member.covered ? 'covered' : 'not covered'}
              </Badge>
            </Row>
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
              {member.has_own_device
                ? 'An adult with an account of their own. They act for themselves; their photos are private to them.'
                : 'No login of their own — persons.account_id IS NULL. You act for them.'}
            </p>
          </div>
        </div>
        <KeyValue
          rows={[
            ['email', member.email],
            ['email_verified', <Bool key="ev" value={member.email_verified} />],
            ['pending_email', member.pending_email],
            ['consent_status', member.consent_status],
            ['enrolment_status', member.enrolment_status],
            ['monitoring_enabled', <Bool key="m" value={member.monitoring_enabled} />],
            // A COUNT, never hit detail (P20).
            ['active_hits', member.active_hits],
            ['photos_visible', <Bool key="pv" value={member.photos_visible} />],
          ]}
        />
        {!member.covered && member.enrolment_status === 'active' && (
          <Note>
            Enrolled but not covered. Coverage needs an active seat, monitoring on, a live
            subscription and an active enrolment — all four.
          </Note>
        )}
      </Panel>

      <SetupPanel
        personId={personId}
        firstName={firstName}
        // FROM THE ROSTER, not from `next`. See the panel's note: `next` skips
        // from consent to 'photos' with no enrolment value in between.
        enrolmentStatus={member.enrolment_status}
        state={state}
      />

      <PicturePanel
        member={member}
        name={name}
        photoNonce={photoNonce}
        onChanged={onChanged}
      />

      <ScorePanel firstName={firstName} state={score} />

      <RecommendationsPanel
        member={member}
        firstName={firstName}
        state={recommendations}
      />

      <PhotosPanel member={member} firstName={firstName} onPhotosChanged={photosChanged} />

      <Raw label="GET /v1/household (this member's row)" value={member} />
    </Screen>
  );
}

/**
 * Where this member is up to. `next` IS THE LOOP and it is server-owned (P23):
 * this panel maps the value to copy and decides nothing about the order.
 *
 * `photo_count` is display only. `attributed_photo_count` is a DIFFERENT number
 * — faces matched to them — which reads 0 until face processing finishes and
 * would send the loop back to the photo step forever if it drove the step.
 *
 * THE ONE THING `next` DOES NOT TELL YOU IS ENROLMENT, and it matters here.
 * `nextStepFor` never returns `'enrolment'` — reading that state would need a
 * cross-boundary read into `svc.v_person_enrolment_state` that `memberState`
 * does not have — so `next` steps from `consent` straight to `'photos'`.
 * Reading `'photos'` as "all that is left is photos" would tell a manager
 * nothing is outstanding for a member who has never been scanned, and a member
 * with no face signature is never monitored.
 *
 * The roster answers it: `enrolment_status` is on the member row this screen
 * already holds. So the two sources are combined HERE, where both are in hand,
 * rather than either being asked to know something it cannot.
 */
function SetupPanel({
  personId,
  firstName,
  enrolmentStatus,
  state,
}: {
  personId: string;
  firstName: string;
  enrolmentStatus: string;
  state: ReturnType<typeof useApi<Awaited<ReturnType<typeof getMemberState>>>>;
}) {
  return (
    <Panel title="setup">
      <Async state={state}>
        {(data) => {
          // `next: 'photos'` with no enrolment really means "face scan owed".
          // The rule is a tested predicate, not a condition in a render.
          const scanOwed = faceScanOwed(data.next, enrolmentStatus);
          const copy = scanOwed
            ? memberNextCopy('enrolment', firstName)
            : memberNextCopy(data.next, firstName);
          return (
            <>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                {copy.title}{' '}
                {copy.done ? (
                  <Badge tone="ok">done</Badge>
                ) : (
                  <Badge tone="warn">{scanOwed ? 'enrolment' : data.next}</Badge>
                )}
              </p>
              <p className="muted" style={{ margin: '0 0 12px' }}>
                {copy.detail}
              </p>
              {scanOwed && (
                <Note>
                  Their <code>next</code> reads <code>{data.next}</code>, which does not account
                  for the face scan &mdash; the api cannot see enrolment from that route. Their
                  <code> enrolment_status</code> is <code>{enrolmentStatus}</code>, so the scan is
                  still owed, and <strong>nothing is monitored for them until it is done</strong>.
                </Note>
              )}
              <KeyValue
                rows={[
                  ['is_minor', <Bool key="im" value={data.is_minor} />],
                  ['required_signer_role', data.required_signer_role],
                  ['quiz_submitted', <Bool key="q" value={data.quiz_submitted} />],
                  ['photo_count', data.photo_count],
                  ['attributed_photo_count', data.attributed_photo_count],
                ]}
              />
              {(scanOwed || (!copy.done && data.next !== 'photos')) && (
                <Row>
                  <a
                    className="action"
                    href={`#/setup/family-member/${encodeURIComponent(personId)}`}
                  >
                    Resume their setup
                  </a>
                </Row>
              )}
              {!scanOwed && data.next === 'photos' && (
                <Note>
                  Consent is signed and they are enrolled, so photos are all that is left &mdash;
                  add them below. That step sits after consent on purpose: a photo of someone
                  becomes a search seed, and seeding before consent is signed is what the
                  enrolment&rsquo;s NOT NULL consent columns exist to prevent.
                </Note>
              )}
              {data.is_minor && (
                <Note>
                  Under 18. Their consent is signed by their guardian, and{' '}
                  <strong>no searching runs for them</strong> — services refuse discovery below 18
                  unconditionally. The roster still reads them as covered, which is optimistic and
                  known.
                </Note>
              )}
              <Raw label="GET /v1/household/members/{personId}" value={data} />
            </>
          );
        }}
      </Async>
    </Panel>
  );
}

/**
 * The profile picture. Two ways to set one, and NEITHER PUTS BYTES THROUGH THE
 * API (P17): from one of their own photos it is a server-side S3 CopyObject,
 * and from a file it is a presigned PUT this browser performs.
 *
 * A pfp is never sourced from the enrolment reference image, even though we
 * hold a face photo of every enrolled person. That image is Article 9
 * biometric data collected under a consent document naming a different
 * purpose, and rendering it as an avatar is that different purpose.
 */
function PicturePanel({
  member,
  name,
  photoNonce,
  onChanged,
}: {
  member: HouseholdMember;
  name: string;
  photoNonce: number;
  onChanged: () => void;
}) {
  const personId = member.person_id;
  const [rejected, setRejected] = useState<string | null>(null);
  // A picker over their OWN photos — the api refuses a photo_id that is not a
  // processed photo of this subject, so the list has to be theirs. Re-reads
  // when a photo lands below, or the picker sits empty right after an upload.
  const photos = useApi(() => listPhotos(undefined, 24, personId), [personId, photoNonce], {
    skip: !member.photos_visible,
  });

  const fromPhoto = useAction(setAvatarFromPhoto, onChanged);
  const fromFile = useAction(uploadAvatar, onChanged);
  const clear = useAction(removeAvatar, onChanged);

  if (!member.photos_visible) {
    return (
      <Panel title="profile picture">
        <Pfp member={member} size={56} />
        <p className="muted" style={{ marginTop: 10 }}>
          {name} has an account of their own, so their picture is theirs to set. The api authorises
          this write with the same predicate it uses for photos.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="profile picture">
      <Row>
        <Pfp member={member} size={72} />
        <div>
          {member.pfp_url === null ? (
            <p className="muted" style={{ margin: 0 }}>
              None set — initials are the normal state, not an error. The first photo to finish
              processing becomes one automatically, unless a picture was ever set or removed here.
            </p>
          ) : (
            <>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                {expiryLabel(member.pfp_expires_at)} · never cached past that, and never persisted
              </p>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                pfp_focus:{' '}
                {member.pfp_focus === null
                  ? 'none — centre-cropped'
                  : `${member.pfp_focus.x.toFixed(2)}, ${member.pfp_focus.y.toFixed(2)}, ${member.pfp_focus.w.toFixed(2)} × ${member.pfp_focus.h.toFixed(2)}`}
              </p>
            </>
          )}
        </div>
      </Row>

      <Row>
        <label className={fromFile.pending ? 'action secondary disabled' : 'action secondary'}>
          {fromFile.pending ? 'uploading…' : 'Upload a picture'}
          <input
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            disabled={fromFile.pending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file === undefined) return;
              const contentType = avatarContentTypeOf(file);
              if (contentType === null) {
                setRejected(`${file.name} — a picture must be JPEG, PNG or WebP`);
                return;
              }
              setRejected(null);
              fromFile.run(file, contentType, personId);
            }}
          />
        </label>
        <Button
          variant="danger"
          onClick={() => clear.run(personId)}
          disabled={clear.pending || member.pfp_url === null}
        >
          {clear.pending ? 'removing…' : 'Remove'}
        </Button>
      </Row>
      <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
        Removing sticks: it also stops the automatic first-photo picture coming back on the next
        upload. It answers 204 even when there was nothing to remove.
      </p>
      {rejected !== null && <p className="muted">{rejected}</p>}
      {fromFile.error !== null && (
        <Failure
          error={fromFile.error}
          hint={
            {
              S3_TRANSPORT:
                'The pfp bucket is new and has never been written to — this is the most likely place a CORS or bucket misconfiguration shows.',
              S3_REJECTED:
                'S3 refused the signed PUT. The URL is signed for one content type and one exact byte count, so re-encoding between the mint and the PUT breaks it.',
              FILE_TOO_LARGE: 'Above AVATAR_MAX_BYTES.',
              FORBIDDEN: 'Not a subject you may manage.',
            }[fromFile.error.code]
          }
        />
      )}
      {clear.error !== null && <Failure error={clear.error} />}

      <h3 className="member-subhead">or use one of their photos</h3>
      <Async state={photos} empty="no photos of theirs yet">
        {(data) =>
          data.photos.length === 0 ? (
            <p className="empty">no photos of theirs yet — add some below first</p>
          ) : (
            <>
              <div className="thumbs">
                {data.photos.map((photo) => (
                  <div className="thumb" key={photo.photo_id}>
                    <img src={photo.url} alt="" loading="lazy" />
                    <div className="meta">
                      <Button
                        variant="secondary"
                        onClick={() => fromPhoto.run(photo.photo_id, personId)}
                        disabled={fromPhoto.pending}
                      >
                        Use this
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
                Copied server-side, S3 to S3 — no bytes pass through the api. The copy is
                independent, so deleting the source photo later leaves the picture in place.
              </p>
            </>
          )
        }
      </Async>
      {fromPhoto.error !== null && (
        <Failure
          error={fromPhoto.error}
          hint={
            fromPhoto.error.code === 'FORBIDDEN'
              ? 'Either not a subject you may manage, or not a processed photo of that person. The two answer alike.'
              : undefined
          }
        />
      )}
    </Panel>
  );
}

/**
 * ALWAYS 200, SOMETIMES NULL — and that is the difference from
 * `GET /v1/me/score`, which 404s NO_QUIZ_RESPONSE and is deliberately left
 * alone. A signed-in user with no quiz is an anomaly; a MEMBER mid-onboarding
 * is the normal state, so P24 applies and `unavailable_reason` renders as a
 * next step rather than a failure.
 *
 * The band is rendered AS SENT. Never re-derived from the number — one
 * implementation computes the score (P13) and a client that bands it itself is
 * the −18 disagreement in a new form.
 */
function ScorePanel({
  firstName,
  state,
}: {
  firstName: string;
  state: ReturnType<typeof useApi<Awaited<ReturnType<typeof getMemberScore>>>>;
}) {
  return (
    <Panel title="likeness health score">
      <Async state={state}>
        {(data) =>
          data.score === null ? (
            <>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                {data.unavailable_reason === 'quiz_outdated'
                  ? `${firstName}'s answers are against an older quiz`
                  : `${firstName} has no score yet`}
              </p>
              <p className="muted" style={{ margin: 0 }}>
                {data.unavailable_reason === 'quiz_outdated'
                  ? 'Their score needs the current questions answered again before it can be computed.'
                  : 'They have not answered their questions. This is the normal state for a new member — a 200 with a reason, not a 404.'}
              </p>
              <Raw label="GET …/score" value={data} />
            </>
          ) : (
            <>
              <div className="dash-score-row">
                <span className="score-number">{data.score.live}</span>
                <span>
                  <Badge>{data.score.band}</Badge>
                  <span className="flow-note" style={{ display: 'block', margin: '6px 0 0' }}>
                    of a possible {data.score.current_ceiling} today · {data.score.scoring_version}{' '}
                    · computed {new Date(data.score.computed_at).toLocaleDateString()}
                  </span>
                </span>
              </div>
              {data.scope_note !== null && <p className="muted">{data.scope_note}</p>}
              <Raw label="GET …/score" value={data} />
            </>
          )
        }
      </Async>
    </Panel>
  );
}

/**
 * `voice` DECIDES WHETHER THESE ARE BUTTONS OR PROSE, and it must be read
 * before rendering:
 *
 *  - `self` — the reader can act. Items carry a `target`, and it becomes a link.
 *  - `ask` — the reader cannot; the member must. Titles read "Ask Mel to…" and
 *    `target` is absent on every item. THAT ABSENCE IS NOT AN OMISSION TO WORK
 *    AROUND: the card that would carry one here is respond_to_hits pointing at
 *    `Reports`, i.e. a link taking a manager into another adult's hit detail,
 *    which P20 forbids. Synthesising a target re-opens it.
 *
 * ACKNOWLEDGE IS NARROWER THAN THE READS. The api authorises it through
 * resolveSubject — yourself, or a subject with no account of their own — so an
 * own-device member answers 403. The control is not rendered rather than
 * rendered and refused: dismissing another adult's card is acting AS them.
 */
function RecommendationsPanel({
  member,
  firstName,
  state,
}: {
  member: HouseholdMember;
  firstName: string;
  state: ReturnType<typeof useApi<Awaited<ReturnType<typeof getMemberRecommendations>>>>;
}) {
  const acknowledge = useAction(acknowledgeMemberRecommendation, state.reload);

  return (
    <Panel title="recommended actions">
      <Async state={state} empty="nothing outstanding">
        {(data) => (
          <>
            <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
              voice: <code>{data.voice}</code> —{' '}
              {data.voice === 'ask'
                ? `${firstName} has their own device, so these are theirs to do. Rendered as information, with no links: the server sends no target in this voice and one must not be invented.`
                : 'you can perform these, so they navigate.'}
            </p>
            {data.recommendations.length === 0 ? (
              <p className="empty">nothing outstanding</p>
            ) : (
              <div className="dash-actions">
                {data.recommendations.map((rec) => {
                  const href = recommendationHref(rec.target);
                  const body = (
                    <span className="dash-action-text">
                      <strong>
                        {rec.title}
                        {rec.urgent && <span className="urgent-badge">Urgent</span>}
                      </strong>
                      <span>{rec.description}</span>
                    </span>
                  );
                  return href === undefined ? (
                    <div key={rec.id} className="dash-action static">
                      {body}
                    </div>
                  ) : (
                    <a key={rec.id} className="dash-action" href={href}>
                      {body}
                      <span className="choice-chevron" aria-hidden="true">
                        ›
                      </span>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Only for a subject who cannot act for themselves, or yourself. */}
            {!member.has_own_device &&
              data.recommendations.some((rec) => rec.id === 'action_review_score') && (
                <Row>
                  <Button
                    variant="secondary"
                    onClick={() => acknowledge.run(member.person_id, 'moderate')}
                    disabled={acknowledge.pending}
                  >
                    {acknowledge.pending ? 'saving…' : 'I have read their risk band (moderate)'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => acknowledge.run(member.person_id, 'high')}
                    disabled={acknowledge.pending}
                  >
                    high
                  </Button>
                </Row>
              )}
            {member.has_own_device && (
              <p className="muted" style={{ fontSize: 13 }}>
                No dismiss control: {firstName} has an account of their own, and ticking off their
                card would be acting as them. The api answers 403.
              </p>
            )}
            {acknowledge.error !== null && <Failure error={acknowledge.error} />}
            <Raw label="GET …/recommendations" value={data} />
          </>
        )}
      </Async>
    </Panel>
  );
}

/**
 * Their photo space. THE LIMIT IS PER PERSON now, so a full grid for the
 * account holder no longer blocks adding photos for a family member — and the
 * `409 LIMIT_EXCEEDED` block describes THAT SUBJECT'S allowance, which is why
 * the copy names them rather than reusing a number from another grid.
 */
function PhotosPanel({
  member,
  firstName,
  onPhotosChanged,
}: {
  member: HouseholdMember;
  firstName: string;
  onPhotosChanged: () => void;
}) {
  const personId = member.person_id;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const uploadKey = useIntentKey();

  const photos = useApi(() => listPhotos(undefined, undefined, personId), [personId], {
    skip: !member.photos_visible,
    pollMs: 10_000,
  });

  const upload = useAction(
    async (files: File[], key: string) => {
      const accepted: { file: File; sha256: string; contentType: PhotoContentType }[] = [];
      const refused: string[] = [];
      for (const file of files) {
        const contentType = photoContentTypeOf(file);
        if (contentType === null) {
          refused.push(`${file.name} — unsupported type`);
          continue;
        }
        accepted.push({ file, sha256: await sha256Hex(file), contentType });
      }
      setRejected(refused);
      if (accepted.length === 0) throw new Error('no supported files selected');
      return uploadPhotos(
        accepted,
        key,
        (done, total) => setProgress(`${String(done)}/${String(total)} uploaded`),
        // BOTH the mint and the commit carry it; uploadPhotos threads it
        // through. A commit that omitted it would file these in YOUR space.
        personId,
      );
    },
    () => {
      uploadKey.next();
      setProgress(null);
      photos.reload();
      // Their `next` flips off 'photos' the moment one lands, and the picture
      // picker above gains a candidate.
      onPhotosChanged();
    },
  );

  const remove = useAction(deletePhotos, () => {
    setSelected(new Set());
    photos.reload();
    onPhotosChanged();
  });

  if (!member.photos_visible) {
    return (
      <Panel title="photos">
        <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
          {firstName}&rsquo;s photos are private to them
        </p>
        <p className="muted" style={{ margin: 0 }}>
          They have an account of their own, so they manage their own space. This is a normal state,
          not an error and not something to retry — the api answers 403, and it answers the same
          403 for a person outside your household and for an id that does not exist.
        </p>
      </Panel>
    );
  }

  const toggle = (photoId: string) => {
    const next = new Set(selected);
    if (next.has(photoId)) next.delete(photoId);
    else next.add(photoId);
    setSelected(next);
  };

  return (
    <Panel title={`photos of ${firstName}`}>
      <Row>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/heic,image/heif,.jpg,.jpeg,.heic,.heif"
          disabled={upload.pending}
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            if (files.length > 0) upload.run(files, uploadKey.key);
          }}
        />
      </Row>
      <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
        Uploaded into <strong>{firstName}&rsquo;s</strong> space against{' '}
        <strong>their</strong> allowance — nothing here counts against yours. Commit key:{' '}
        <code>{uploadKey.key}</code>
      </p>
      {progress !== null && <p className="muted">{progress}</p>}
      {rejected.length > 0 && <p className="muted">skipped: {rejected.join(', ')}</p>}
      {upload.error !== null && (
        <Failure
          error={upload.error}
          hint={
            upload.error.code === 'LIMIT_EXCEEDED'
              ? photoLimitCopy(limitDetailOf(upload.error.body), firstName)
              : {
                  FILE_TOO_LARGE: 'Above the per-file ceiling in config.',
                  FORBIDDEN:
                    'Not a subject you may manage. An own-device member, someone outside your household and a non-existent id all answer this way.',
                  UPLOAD_NOT_FOUND: 'A minted upload expired before the commit — mint again.',
                  S3_TRANSPORT:
                    'The uploads bucket almost certainly does not allow PUT from this origin yet.',
                }[upload.error.code]
          }
        />
      )}

      <Async state={photos} empty="none yet">
        {(data) => (
          <>
            {data.photos.length === 0 ? (
              <p className="empty">
                none yet — 200 with an empty list, not a 404. This is what makes their{' '}
                <code>next</code> read <code>photos</code>.
              </p>
            ) : (
              <div className="thumbs">
                {data.photos.map((photo) => (
                  <div className="thumb" key={photo.photo_id}>
                    <img src={photo.url} alt="" loading="lazy" />
                    <div className="meta">
                      <div>
                        {photo.protected ? (
                          <Badge tone="ok">protected</Badge>
                        ) : (
                          <Badge tone="warn">unprotected</Badge>
                        )}
                      </div>
                      {photo.unprotected_reason !== null && (
                        <div className="muted">{photo.unprotected_reason}</div>
                      )}
                      {/* FACES, NOT PEOPLE — a box per detection, so a face on
                          a poster or one picked up twice both count. */}
                      <div className="muted">
                        {photo.faces_total === 0
                          ? 'no faces detected'
                          : `${String(photo.faces_covered)} of ${String(photo.faces_total)} ${photo.faces_total === 1 ? 'face' : 'faces'} monitored`}
                      </div>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected.has(photo.photo_id)}
                          onChange={() => toggle(photo.photo_id)}
                        />
                        select
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Row>
              <Button
                variant="danger"
                onClick={() => remove.run([...selected], personId)}
                disabled={remove.pending || selected.size === 0}
              >
                {remove.pending ? 'deleting…' : `Delete ${String(selected.size)} selected`}
              </Button>
            </Row>
            {remove.error !== null && <Failure error={remove.error} />}
            {/* The echoed subject, which is the point of the field: it says
                which member's grid this response is, rather than the client
                remembering alongside the request. */}
            <p className="muted" style={{ fontSize: 13 }}>
              response subject_person_id: <code>{data.subject_person_id}</code>
            </p>
            <Raw label="GET /v1/photos?subject_person_id=…" value={data} />
          </>
        )}
      </Async>
    </Panel>
  );
}
